import * as React from 'react'
import { cn } from '@/lib/utils.js'

/**
 * Select and Textarea.
 *
 * ── WHY THIS IS A NATIVE `<select>` AND NOT RADIX ───────────────────────────
 * D4 chose shadcn/ui for the components most often got wrong, and a listbox is
 * genuinely one of them — but only when it needs to do something native
 * selects cannot: search, multi-select, rich option rendering. None of the
 * choices in this phase do. They are short, closed enum lists: seven stages,
 * five statuses, eleven job types.
 *
 * For those, the native control is the better product decision. It gets the
 * platform picker on iOS and Android — a scroll wheel a contractor can operate
 * one-handed with gloves on — rather than a custom popup that only approximates
 * one. Reaching for the fancier component here would make the mobile
 * experience worse in exchange for looking more considered.
 *
 * Radix stays available for the cases that actually need it.
 */

export const Select = React.forwardRef(function Select (
  { className, invalid, children, ...props }, ref
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-tap w-full appearance-none rounded-md border bg-surface pl-3 pr-9 text-base sm:text-sm',
          'border-line-strong shadow-card',
          'transition-[border-color,box-shadow] duration-150',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
          invalid && 'border-danger',
          className
        )}
        {...props}
      >
        {children}
      </select>
      {/* Inline SVG chevron rather than an icon component: it must sit inside
          the control's padding box and never intercept the click. */}
      <svg
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
        viewBox="0 0 16 16" fill="none" aria-hidden="true"
      >
        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
})

export const Textarea = React.forwardRef(function Textarea (
  { className, invalid, ...props }, ref
) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-md border bg-surface px-3 py-2 text-base sm:text-sm',
        'border-line-strong shadow-card',
        'placeholder:text-ink-subtle',
        'transition-[border-color,box-shadow] duration-150',
        'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
        invalid && 'border-danger',
        className
      )}
      rows={props.rows ?? 3}
      {...props}
    />
  )
})
